// frontend/src/pages/ChatDetail.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom"; // 🔥 AGREGADO useLocation
import { useAuth } from "../context/AuthContext";
import chatService from "../services/chatService";
import presenceService from "../services/presenceService";

import {
  ArrowLeft,
  Send,
  Loader2,
  CheckCheck,
  Check,
  Phone,
  MoreVertical,
  Sparkles, // 🔥 NUEVO: Icono para mensaje sugerido
} from "lucide-react";
import Swal from "sweetalert2";
import ReportChatButton from "../components/ReportChatButton";

export default function ChatDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // 🔥 NUEVO
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [userPresence, setUserPresence] = useState(null);
  const [initialMessage, setInitialMessage] = useState(null); // 🔥 NUEVO

  // 👇 HEARTBEAT - Enviar latido cada 30 segundos
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        presenceService.sendHeartbeat();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // 🔥 LEER MENSAJE INICIAL DE LA URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const msg = params.get("initial_message");

    if (msg) {
      const decodedMessage = decodeURIComponent(msg);
      setInitialMessage(decodedMessage);
      setNewMessage(decodedMessage);

      // Limpiar la URL para que no se quede el parámetro
      window.history.replaceState({}, "", `/chat/${id}`);

      // Enfocar el input después de un breve delay
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Colocar el cursor al final del texto
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }, 500);
    }
  }, [id, location.search]);

  useEffect(() => {
    if (id && id !== "undefined") {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
      Swal.fire("Conversa inválida", "", "error").then(() => navigate("/chat"));
    }
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!loading && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [loading]);

  // Detectar teclado en mobile
  useEffect(() => {
    const handleResize = () => {
      const isKeyboard = window.innerHeight < 600;
      setIsKeyboardOpen(isKeyboard);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 👇 OBTENER PRESENCIA DEL OTRO USUARIO
  useEffect(() => {
    const otherUser = getOtherUser();
    if (otherUser?.id) {
      fetchUserPresence(otherUser.id);
      const interval = setInterval(() => {
        fetchUserPresence(otherUser.id);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [conversation]);

  const fetchUserPresence = async (userId) => {
    try {
      const presence = await presenceService.getUserPresence(userId);
      setUserPresence(presence);
    } catch (error) {
      console.error("Error fetching user presence:", error);
    }
  };

  const fetchMessages = async () => {
    if (!id || id === "undefined") return;

    try {
      const response = await chatService.getMessages(id);
      setConversation(response.data.conversation);
      setMessages(response.data.messages);
    } catch (error) {
      console.error("Error fetching messages:", error);
      if (error.response?.status === 404) {
        Swal.fire("Conversa não encontrada", "", "error").then(() =>
          navigate("/chat")
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await chatService.sendMessage(id, newMessage.trim());
      setMessages([...messages, response.data]);
      setNewMessage("");
      setInitialMessage(null); // 🔥 Limpiar mensaje inicial después de enviar
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error("Error sending message:", error);
      Swal.fire("Erro!", "Não foi possível enviar a mensagem.", "error");
    } finally {
      setSending(false);
    }
  };

  const getOtherUser = () => {
    if (!conversation) return null;
    return conversation.buyer_id === user?.id
      ? conversation.seller
      : conversation.buyer;
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (date) => {
    const now = new Date();
    const msgDate = new Date(date);
    const diff = now - msgDate;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return "Hoje";
    if (days === 1) return "Ontem";
    if (days < 7) return `${days} dias atrás`;
    return msgDate.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
    });
  };

  const isToday = (date) => {
    const now = new Date();
    const msgDate = new Date(date);
    return now.toDateString() === msgDate.toDateString();
  };

  const groupMessagesByDate = () => {
    const groups = [];
    let currentDate = null;
    let currentGroup = [];

    messages.forEach((msg) => {
      const msgDate = new Date(msg.created_at).toDateString();
      if (msgDate !== currentDate) {
        if (currentGroup.length > 0) {
          groups.push({ date: currentDate, messages: currentGroup });
        }
        currentDate = msgDate;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ date: currentDate, messages: currentGroup });
    }

    return groups;
  };

  const otherUser = getOtherUser();

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100 z-50">
        <Loader2 className="w-10 h-10 text-[#449879] animate-spin" />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100 z-50">
        <div className="text-center">
          <p className="text-gray-500">Conversa não encontrada</p>
          <button
            onClick={() => navigate("/chat")}
            className="mt-4 text-[#449879] hover:underline"
          >
            Voltar para conversas
          </button>
        </div>
      </div>
    );
  }

  const isOnline = userPresence?.is_online || false;

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col z-50">
      {/* ===== HEADER CON ESTADO DE PRESENCIA ===== */}
      {/* ===== HEADER CON ESTADO DE PRESENCIA ===== */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-gray-100/80 px-3 py-2 flex items-center gap-2 flex-shrink-0 shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
          title="Voltar"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>

        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#449879] to-[#2d5f4d] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm ring-2 ring-[#449879]/20">
          {otherUser?.name?.charAt(0).toUpperCase() || "?"}
        </div>

        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-gray-800 text-sm truncate leading-tight">
            {otherUser?.name || "Usuário"}
          </h2>
          <div className="flex items-center gap-1.5 text-[10px]">
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full ${
                isOnline ? "bg-green-500 animate-pulse" : "bg-gray-400"
              }`}
            ></span>
            <span
              className={`font-medium ${
                isOnline ? "text-green-600" : "text-gray-400"
              }`}
            >
              {isOnline ? "Online" : "Offline"}
            </span>
            {!isOnline && userPresence?.last_seen && (
              <span className="text-gray-400 text-[9px]">
                • Visto há{" "}
                {Math.floor(
                  (new Date() - new Date(userPresence.last_seen)) / 60000
                )}{" "}
                min
              </span>
            )}
            <span className="text-gray-300 text-[9px]">•</span>
            <span className="truncate max-w-[80px] text-gray-400/80 text-[9px]">
              📦 {conversation?.post?.titulo?.substring(0, 15) || "Produto"}
            </span>
          </div>
        </div>

        {/* 🔥 BOTONES DE ACCIÓN */}
        <div className="flex items-center gap-0.5">
          {/* 🔥 NUEVO: Botón de denunciar conversación */}
          <ReportChatButton
            conversationId={conversation?.id}
            otherUserName={otherUser?.name || otherUser?.apelido}
            postTitle={conversation?.post?.titulo}
          />

          <button className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
            <Phone className="w-4 h-4 text-gray-500" />
          </button>
          <button className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
            <MoreVertical className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>
      {/* ===== MENSAJES ===== */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-3 py-3 space-y-2"
        style={{
          scrollBehavior: "smooth",
          paddingBottom: "8px",
        }}
      >
        {groupMessagesByDate().map((group, groupIndex) => (
          <div key={groupIndex} className="space-y-1.5">
            {/* Separador de fecha */}
            <div className="flex justify-center my-4">
              <span className="text-[10px] font-medium text-gray-400 bg-white/70 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm border border-gray-100/50">
                {isToday(new Date(group.date))
                  ? "Hoje"
                  : formatDate(group.date)}
              </span>
            </div>

            {group.messages.map((msg, msgIndex) => {
              const isMine = msg.sender_id === user?.id;
              const showAvatar =
                !isMine &&
                (msgIndex === 0 ||
                  group.messages[msgIndex - 1]?.sender_id !== msg.sender_id);

              return (
                <div
                  key={msg.id}
                  className={`flex ${
                    isMine ? "justify-end" : "justify-start"
                  } items-end gap-1.5`}
                >
                  {/* Avatar del otro usuario */}
                  {!isMine && showAvatar && (
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#449879] to-[#2d5f4d] flex items-center justify-center text-white font-medium text-[10px] flex-shrink-0 mb-1">
                      {otherUser?.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                  )}

                  {!isMine && !showAvatar && (
                    <div className="w-7 flex-shrink-0"></div>
                  )}

                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${
                      isMine
                        ? "bg-[#449879] text-white rounded-br-sm shadow-sm shadow-[#449879]/10"
                        : "bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100/50"
                    }`}
                  >
                    <p className="text-sm leading-relaxed break-words">
                      {msg.message}
                    </p>
                    <div
                      className={`flex items-center justify-end gap-1 mt-0.5 ${
                        isMine ? "text-green-100/60" : "text-gray-400"
                      }`}
                    >
                      <span className="text-[9px] font-medium">
                        {formatTime(msg.created_at)}
                      </span>
                      {isMine && (
                        <span className="text-[9px]">
                          {msg.is_read ? (
                            <CheckCheck className="w-2.5 h-2.5 text-green-200/70" />
                          ) : (
                            <Check className="w-2.5 h-2.5 text-green-200/50" />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* ===== INPUT CON INDICADOR DE MENSAJE INICIAL ===== */}
      <div
        className={`bg-white/95 backdrop-blur-sm border-t border-gray-100/80 px-3 py-2 flex-shrink-0 shadow-lg shadow-black/5 ${
          isKeyboardOpen ? "pb-3" : "pb-3"
        }`}
      >
        {/* 🔥 INDICADOR DE MENSAJE SUGERIDO */}
        {initialMessage && (
          <div className="flex items-center gap-2 px-3 py-1.5 mb-1.5 bg-blue-50 border border-blue-100 rounded-xl max-w-4xl mx-auto">
            <Sparkles className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
            <p className="text-xs text-blue-600">
              💡 Mensagem sugerida — você pode editar antes de enviar
            </p>
            <button
              onClick={() => {
                setNewMessage("");
                setInitialMessage(null);
              }}
              className="ml-auto text-blue-400 hover:text-blue-600 transition-colors text-xs font-medium"
            >
              Descartar
            </button>
          </div>
        )}

        <form
          onSubmit={handleSendMessage}
          className="flex gap-2 items-end max-w-4xl mx-auto"
        >
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Digite sua mensagem..."
              className={`w-full px-4 py-2.5 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#449879]/40 focus:border-[#449879] resize-none text-sm min-h-[40px] max-h-[100px] transition-all duration-200 ${
                initialMessage
                  ? "border-blue-300 bg-blue-50/50 focus:bg-white"
                  : "border-gray-200 bg-gray-50/80 focus:bg-white"
              }`}
              rows="1"
              disabled={sending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              style={{
                height: "auto",
                minHeight: "40px",
                maxHeight: "100px",
              }}
            />
            {/* Indicador de mensaje sugerido en el input */}
            {initialMessage && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Sparkles className="w-4 h-4 text-blue-400 animate-pulse" />
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className={`text-white p-2.5 rounded-full transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0 w-11 h-11 hover:scale-105 active:scale-95 shadow-lg ${
              initialMessage
                ? "bg-blue-500 hover:bg-blue-600 shadow-blue-500/20"
                : "bg-[#449879] hover:bg-[#367d64] shadow-[#449879]/20"
            }`}
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
